import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import { Shield } from 'lucide-react';

export default function Privacy() {
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto">
      <SEO title="Privacy Policy" description="How KoreanHub collects, uses and protects your data." path="/privacy" />
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-14 h-14 bg-coral-100 dark:bg-coral-900/30 rounded-xl flex items-center justify-center">
          <Shield className="w-7 h-7 text-coral-600" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {isKorean ? '개인정보처리방침' : 'Privacy Policy'}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {isKorean ? '최종 업데이트: 2026년 2월 1일' : 'Last updated: February 1, 2026'}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 sm:p-8 shadow-lg prose dark:prose-invert max-w-none">
        {isKorean ? (
          <>
            <h2>1. 개요</h2>
            <p>
              KoreanHub.net("당사", "우리")은 사용자의 개인정보를 소중히 여기며, 본 개인정보처리방침은 당사가 수집하는 정보의 종류, 사용 방법, 보호 방법을 설명합니다.
            </p>

            <h2>2. 수집하는 정보</h2>
            <h3>2.1 직접 제공하는 정보</h3>
            <ul>
              <li>계정 정보: 이름, 이메일 주소, 비밀번호</li>
              <li>프로필 정보: 프로필 사진, 자기소개, 현재 위치</li>
              <li>콘텐츠: 게시물, 댓글, 리뷰, 사진</li>
              <li>커뮤니케이션: 고객 지원 문의, 피드백</li>
            </ul>

            <h3>2.2 자동으로 수집되는 정보</h3>
            <ul>
              <li>기기 정보: IP 주소, 브라우저 유형, 운영 체제</li>
              <li>사용 정보: 방문 페이지, 클릭한 링크, 이용 시간</li>
              <li>쿠키 및 유사 기술: 세션 관리, 환경 설정 저장</li>
            </ul>

            <h2>3. 정보 사용 목적</h2>
            <ul>
              <li>서비스 제공 및 개선</li>
              <li>계정 생성 및 관리</li>
              <li>고객 지원 제공</li>
              <li>서비스 관련 공지 발송</li>
              <li>부정 사용 방지 및 보안 유지</li>
              <li>법적 의무 준수</li>
            </ul>

            <h2>4. 정보 공유</h2>
            <p>당사는 다음의 경우에만 개인정보를 제3자와 공유합니다:</p>
            <ul>
              <li>사용자의 동의가 있는 경우</li>
              <li>서비스 제공업체와의 협력 (결제 처리, 이메일 발송 등)</li>
              <li>법적 요구사항 준수</li>
              <li>사업 양도 시 (인수, 합병)</li>
            </ul>

            <h2>5. 정보 보안</h2>
            <p>
              당사는 업계 표준 보안 조치를 사용하여 개인정보를 보호합니다. 여기에는 암호화, 보안 서버, 접근 제어가 포함됩니다. 그러나 인터넷을 통한 전송은 100% 안전하지 않으므로 절대적인 보안을 보장할 수 없습니다.
            </p>

            <h2>6. 사용자 권리</h2>
            <p>사용자는 다음과 같은 권리를 가집니다:</p>
            <ul>
              <li>개인정보 열람 및 사본 요청</li>
              <li>부정확한 정보 수정 요청</li>
              <li>개인정보 삭제 요청</li>
              <li>정보 처리 제한 요청</li>
              <li>마케팅 수신 거부</li>
            </ul>

            <h2>7. 쿠키</h2>
            <p>
              당사는 쿠키를 사용하여 서비스를 개선합니다. 브라우저 설정에서 쿠키를 비활성화할 수 있지만, 일부 기능이 제한될 수 있습니다.
            </p>

            <h2>8. 아동 개인정보</h2>
            <p>
              당사 서비스는 만 13세 미만의 아동을 대상으로 하지 않습니다. 만 13세 미만 아동의 개인정보를 수집한 것을 알게 되면 즉시 삭제합니다.
            </p>

            <h2>9. 정책 변경</h2>
            <p>
              본 방침은 수시로 업데이트될 수 있습니다. 중요한 변경 사항은 서비스 내 공지 또는 이메일로 안내합니다.
            </p>

            <h2>10. 문의</h2>
            <p>
              개인정보 관련 문의사항은 <Link to="/contact">문의 양식</Link>을 통해 연락해 주세요.
            </p>
          </>
        ) : (
          <>
            <h2>1. Introduction</h2>
            <p>
              KoreanHub.net ("we", "our", "us") values your privacy. This Privacy Policy explains what information we collect, how we use it, and how we protect it.
            </p>

            <h2>2. Information We Collect</h2>
            <h3>2.1 Information You Provide</h3>
            <ul>
              <li>Account information: name, email address, password</li>
              <li>Profile information: profile picture, bio, current location</li>
              <li>Content: posts, comments, reviews, photos</li>
              <li>Communications: customer support inquiries, feedback</li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <ul>
              <li>Device information: IP address, browser type, operating system</li>
              <li>Usage information: pages visited, links clicked, time spent</li>
              <li>Cookies and similar technologies: session management, preference storage</li>
            </ul>

            <h2>3. How We Use Your Information</h2>
            <ul>
              <li>Provide and improve our services</li>
              <li>Create and manage your account</li>
              <li>Provide customer support</li>
              <li>Send service-related notifications</li>
              <li>Prevent fraud and maintain security</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2>4. Information Sharing</h2>
            <p>We share personal information only in these circumstances:</p>
            <ul>
              <li>With your consent</li>
              <li>With service providers (payment processing, email delivery)</li>
              <li>To comply with legal requirements</li>
              <li>In connection with business transfers (acquisition, merger)</li>
            </ul>

            <h2>5. Data Security</h2>
            <p>
              We use industry-standard security measures to protect your information, including encryption, secure servers, and access controls. However, no transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2>6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access and request a copy of your personal data</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal data</li>
              <li>Request restriction of processing</li>
              <li>Opt-out of marketing communications</li>
            </ul>

            <h2>7. Cookies</h2>
            <p>
              We use cookies to improve our services. You can disable cookies in your browser settings, but some features may be limited.
            </p>

            <h2>8. Children's Privacy</h2>
            <p>
              Our service is not intended for children under 13. If we learn that we have collected personal information from a child under 13, we will delete it promptly.
            </p>

            <h2>9. Changes to This Policy</h2>
            <p>
              We may update this policy from time to time. We will notify you of significant changes through in-service notices or email.
            </p>

            <h2>10. Contact Us</h2>
            <p>
              For privacy-related inquiries, please reach us through our <Link to="/contact">contact form</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
